import { useMemo, useState } from 'react';
import { initials, matches, useContacts, type Entry } from '../lib/contacts';
import { displayNumber } from '../lib/phone';
import { NgnBadge, useSheets } from './ContactSheets';
import { PlusIcon, SearchIcon } from './Icons';

function Row({ e }: { e: Entry }) {
  const { openDetail } = useSheets();
  const primary = e.numbers[0];
  return (
    <li className="row interactive" data-testid="contact-row" data-kind={e.kind}>
      <span className={`row-avatar ${e.kind === 'customer' ? 'ngn' : ''}`}>{initials(e.name)}</span>
      <button type="button" className="row-main" onClick={() => openDetail(e)}>
        <span className="row-title" style={{ fontWeight: 500 }}>
          <span className="name">{e.name}</span>
          {e.kind === 'customer' && <NgnBadge />}
        </span>
        <span className="row-sub">
          {[e.kind !== 'customer' && e.company, primary ? displayNumber(primary.number) : 'No phone'].filter(Boolean).join(' · ')}
        </span>
      </button>
    </li>
  );
}

export function Contacts() {
  const { entries, loaded, error, customersSyncedAt } = useContacts();
  const { openEditor } = useSheets();
  const [q, setQ] = useState('');

  const filtered = useMemo(() => entries.filter((e) => matches(e, q)), [entries, q]);
  const mine = filtered.filter((e) => e.kind === 'personal');
  const shared = filtered.filter((e) => e.kind !== 'personal'); // shared + NGN customers, alphabetical

  return (
    <div className="list" data-testid="contacts">
      <div className="list-head">
        <h2 className="large-title">Contacts</h2>
        <button type="button" className="icon-btn" aria-label="New contact" data-testid="contact-add" onClick={() => openEditor({})}>
          <PlusIcon />
        </button>
      </div>
      <label className="search">
        <SearchIcon />
        <input type="search" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search contacts" data-testid="contact-search" />
      </label>

      {!loaded && <div className="empty">Loading…</div>}
      {error && <div className="empty">{error}</div>}

      {loaded && (
        <>
          <h3 className="section-title">My Contacts</h3>
          {mine.length ? (
            <ul className="group contacts">{mine.map((e) => <Row key={e.id} e={e} />)}</ul>
          ) : (
            <p className="section-foot">{q ? 'No matches.' : 'Contacts you save appear here — only you can see them.'}</p>
          )}

          <h3 className="section-title">Shared</h3>
          {shared.length ? (
            <ul className="group contacts">{shared.map((e) => <Row key={`${e.kind}:${e.id}`} e={e} />)}</ul>
          ) : (
            <p className="section-foot">{q ? 'No matches.' : 'No shared contacts yet.'}</p>
          )}
          {customersSyncedAt && (
            <p className="sync-note">NGN Customers synced from Xero · {new Date(customersSyncedAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</p>
          )}
        </>
      )}
    </div>
  );
}
